// src/pages/workflow/ConfigFieldEditor.tsx
// Renders typed config widgets based on a NodeType's config_fields definition.
// Falls back to raw JSON editor when no config_fields are defined.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Badge } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { ConfigField as GeneratedConfigField } from '@core/types/workflow/ConfigField';
import { AnchoredMenu } from '@/components/AnchoredMenu';

// --- Types ---

// The generated types, not a local copy of them. A hand-kept duplicate looks
// harmless until the proto gains a field: the copy silently lacks it, the
// editor silently ignores it, and nothing fails to say so — which is exactly
// how visible_when went unnoticed here.
type ConfigField = Partial<GeneratedConfigField> & {
  key: string;
  label: string;
  type: string;
  required: boolean;
};

interface ConfigFieldEditorProps {
  configFields: ConfigField[];
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

// --- Resource Picker ---

interface ResourcePickerProps {
  field: ConfigField;
  value: string;
  onChange: (value: string) => void;
}

function ResourcePicker({ field, value, onChange }: ResourcePickerProps) {
  const { t } = useI18n();
  const scope = useScope();
  const [items, setItems] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const displayField = field.resource_display_field || 'name';

  const fetchResources = useCallback(async (query: string) => {
    if (!field.resource_endpoint) return;
    setIsLoading(true);
    try {
      // Build the endpoint URL with scope
      let endpoint = field.resource_endpoint;
      endpoint = endpoint.replace('{platform_id}', scope.platformId || '');
      endpoint = endpoint.replace('{tenant_id}', scope.tenantId || '');
      endpoint = endpoint.replace('{product_id}', scope.productId || '');

      const params = new URLSearchParams();
      if (query) params.set('search', query);
      params.set('limit', '20');
      const url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${params.toString()}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || data.data || []);
      }
    } catch {
      // Silently fail — user can still type ID manually
    } finally {
      setIsLoading(false);
    }
  }, [field.resource_endpoint, scope]);

  // Load initial items when opened
  useEffect(() => {
    if (isOpen) {
      fetchResources(search);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve selected value label
  useEffect(() => {
    if (value && items.length > 0) {
      const found = items.find((item) => getItemId(item) === value);
      if (found) setSelectedLabel(found[displayField] || value);
    }
  }, [value, items, displayField]);

  const getItemId = (item: any): string => {
    return item?.entity?.id || item?.id || '';
  };

  const handleSearch = (q: string) => {
    setSearch(q);
    fetchResources(q);
  };

  const handleSelect = (item: any) => {
    const id = getItemId(item);
    onChange(id);
    setSelectedLabel(item[displayField] || id);
    setIsOpen(false);
    setSearch('');
  };


  // Outside click, Escape and repositioning live in AnchoredMenu.

  return (
    <div className="relative" ref={containerRef}>
      <div
        ref={triggerRef}
        className="flex items-center gap-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:border-primary/40 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg className="h-4 w-4 text-muted-foreground flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span className={value ? 'text-foreground flex-1 truncate' : 'text-muted-foreground flex-1'}>
          {value ? (selectedLabel || value) : (field.placeholder || t('workflow.selectResource'))}
        </span>
        {value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(''); setSelectedLabel(''); }}
            className="text-muted-foreground hover:text-foreground"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      <AnchoredMenu
        anchor={triggerRef}
        ignoreRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        role="none"
        matchTriggerWidth
        maxHeight={320}
        className="overflow-hidden"
      >
        <div className="p-2 border-b border-border">
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('workflow.searchResources')}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto max-h-48">
          {isLoading && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              {t('workflow.loading')}
            </div>
          )}
          {!isLoading && items.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              {t('workflow.noResults')}
            </div>
          )}
          {!isLoading && items.map((item, i) => {
            const id = getItemId(item);
            const label = item[displayField] || id;
            const isSelected = id === value;
            return (
              <button
                key={id || i}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex items-center gap-2 ${isSelected ? 'bg-accent text-accent-foreground' : ''}`}
                onClick={() => handleSelect(item)}
              >
                <span className="truncate flex-1">{label}</span>
                {item.description && (
                  <span className="text-xs text-muted-foreground truncate max-w-[120px]">{item.description}</span>
                )}
                {isSelected && (
                  <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
        {/* Manual entry fallback */}
        <div className="p-2 border-t border-border">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('workflow.orEnterIdManually')}
            className="w-full rounded-md border border-input bg-muted/50 px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </AnchoredMenu>
    </div>
  );
}

// --- Key-Value Editor ---

interface KeyValueEditorProps {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  placeholder?: string;
}

function KeyValueEditor({ value, onChange }: KeyValueEditorProps) {
  const { t } = useI18n();
  const entries = Object.entries(value || {});

  const addEntry = () => {
    onChange({ ...value, '': '' });
  };

  const updateKey = (_oldKey: string, newKey: string, index: number) => {
    const newValue: Record<string, string> = {};
    entries.forEach(([k, v], i) => {
      newValue[i === index ? newKey : k] = v;
    });
    onChange(newValue);
  };

  const updateVal = (key: string, newVal: string) => {
    onChange({ ...value, [key]: newVal });
  };

  const removeEntry = (key: string) => {
    const newValue = { ...value };
    delete newValue[key];
    onChange(newValue);
  };

  return (
    <div className="space-y-1.5">
      {entries.map(([k, v], i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <input
            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            value={k}
            onChange={(e) => updateKey(k, e.target.value, i)}
            placeholder="key"
          />
          <input
            className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            value={v}
            onChange={(e) => updateVal(k, e.target.value)}
            placeholder="value"
          />
          <button
            onClick={() => removeEntry(k)}
            className="text-muted-foreground hover:text-destructive p-0.5"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ))}
      <button
        onClick={addEntry}
        className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
      >
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {t('workflow.addKeyValue')}
      </button>
    </div>
  );
}

// --- Field Renderer ---

function ConfigFieldWidget({
  field,
  value,
  onChange,
}: {
  field: ConfigField;
  value: any;
  onChange: (value: any) => void;
}) {
  const { t } = useI18n();

  // The API sends the enum by its full name — CONFIG_FIELD_TYPE_STRING — because
  // that is what the generated MarshalJSON writes. Matched against the short
  // names below, nothing matched and every field fell through to a plain text
  // box: selects, checkboxes and resource pickers all rendered as free text.
  // It went unnoticed only because no node type has config fields seeded yet,
  // so the editor always took the raw-JSON path instead.
  const kind = fieldKind(field);

  switch (kind) {
    case 'STRING':
    case 'EXPRESSION':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );

    case 'TEXT':
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
        />
      );

    case 'NUMBER':
      return (
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
          placeholder={field.placeholder}
        />
      );

    case 'BOOLEAN':
      return (
        <ToggleSwitch
          label={field.label}
          description={field.description}
          checked={!!value}
          onChange={(checked) => onChange(checked)}
        />
      );

    case 'SELECT':
      return (
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)} options={(field.options || []).map((o) => ({ value: o.value, label: o.label }))} placeholder={field.placeholder || t('workflow.selectOption')} />
      );

    case 'JSON':
      return (
        <Textarea
          value={typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              onChange(e.target.value);
            }
          }}
          placeholder={field.placeholder || '{}'}
          rows={4}
          className="font-mono text-xs"
        />
      );

    case 'RESOURCE_REF':
      return (
        <ResourcePicker
          field={field}
          value={value || ''}
          onChange={onChange}
        />
      );

    case 'CREDENTIAL_REF':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || t('workflow.credentialIdPlaceholder')}
        />
      );

    case 'KEY_VALUE':
      return (
        <KeyValueEditor
          value={typeof value === 'object' && value !== null ? value : {}}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      );

    case 'CODE':
      return (
        <div>
          {field.code_language && (
            <Badge variant="secondary" size="sm" className="mb-1.5">{field.code_language}</Badge>
          )}
          <Textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || `// ${field.code_language || 'code'}`}
            rows={6}
            className="font-mono text-xs"
          />
        </div>
      );

    default:
      return (
        <Input
          value={typeof value === 'string' ? value : JSON.stringify(value || '')}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
  }
}

// --- Main Component ---

/**
 * Whether a field belongs in the form given what is currently filled in.
 *
 * A real connector declares far more fields than are ever relevant at once —
 * the ones needed to send a message have nothing to do with the ones needed to
 * send a photo. Shown all at once the form is unusable, so each field states
 * the circumstances it belongs in.
 *
 * Every condition must hold; within one condition any listed value will do.
 */
/** The field's kind without the enum prefix the API sends it with. */
function fieldKind(field: ConfigField): string {
  return (field.type || '').replace(/^CONFIG_FIELD_TYPE_/, '');
}

export function isFieldVisible(field: ConfigField, config: Record<string, any>): boolean {
  const conditions = field.visible_when;
  if (!conditions || conditions.length === 0) return true;

  return conditions.every((condition) => {
    const current = config?.[condition.field];
    const matches = (condition.equals || []).some(
      (candidate) => String(current ?? '') === String(candidate),
    );
    return condition.negate ? !matches : matches;
  });
}

export function ConfigFieldEditor({ configFields, config, onChange }: ConfigFieldEditorProps) {
  const { t } = useI18n();

  // No config_fields defined — fall back to raw JSON
  if (!configFields || configFields.length === 0) {
    return (
      <div>
        <label className="text-sm font-medium text-foreground mb-1 block">{t('workflow.config')}</label>
        <Textarea
          value={JSON.stringify(config || {}, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Invalid JSON
            }
          }}
          rows={6}
          className="font-mono text-xs"
        />
      </div>
    );
  }

  // Drop the fields that do not belong under the current configuration, then
  // sort and group what remains.
  //
  // A hidden field keeps its value rather than being cleared, so switching an
  // operation back and forth does not discard what was already typed. The
  // engine ignores config keys whose field is not visible, so a leftover value
  // from a discarded branch never reaches the integration.
  const visible = configFields.filter((field) => isFieldVisible(field, config || {}));
  const sorted = [...visible].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Group fields
  const groups: Record<string, ConfigField[]> = {};
  for (const field of sorted) {
    const group = field.group || '';
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
  }

  const updateField = (key: string, value: any) => {
    onChange({ ...config, [key]: value });
  };

  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([groupName, fields]) => (
        <div key={groupName}>
          {groupName && (
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 tracking-wide">
              {groupName}
            </h4>
          )}
          <div className="space-y-3">
            {fields.map((field) => (
              <div key={field.key}>
                {fieldKind(field) !== 'BOOLEAN' && (
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    {field.label}
                    {field.required && <span className="text-destructive ml-0.5">*</span>}
                  </label>
                )}
                {field.description && fieldKind(field) !== 'BOOLEAN' && (
                  <p className="text-xs text-muted-foreground mb-1.5">{field.description}</p>
                )}
                <ConfigFieldWidget
                  field={field}
                  value={config[field.key] ?? field.default_value}
                  onChange={(val) => updateField(field.key, val)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ConfigFieldEditor;
